import type { CanvasDoc, Effect, Frame, NoteTarget, PropSpec, PropValue, ThemeToken, Tour } from '../store/types';

const none: Effect = { kind: 'none' };

/** A fresh annotated onboarding canvas showcasing every feature. */
export function onboardingDoc(): CanvasDoc {
  zc = 0;
  const tours: Tour[] = [
    { id: 'tour-guide', title: 'Walkthrough' },
  ];

  // Column layout: 7 topic columns left→right, pitch = 700px.
  // Column i starts at x = 80 + i * 700.
  // Col 0 = Basics, Col 1 = Living Components, Col 2 = TSX, Col 3 = Agent+code,
  // Col 4 = Effects, Col 5 = Annotations, Col 6 = Themes

  const frames: Frame[] = [
    // ── Col 0 (Basics) ─────────────────────────────────────── x=80
    free('welcome', 80, 80, 360,
      `<div style="padding:24px;font-family:system-ui">
        <h1 style="margin:0 0 8px;font-size:24px">Welcome to codeframe</h1>
        <p style="margin:0;color:#555">An infinite canvas where every frame is live, editable HTML. Pan with scroll/drag, zoom with ⌘+scroll. Follow the numbered tours to learn the rest.</p>
      </div>`),
    note('n-b1', 80, 280, 230, 'tour-guide', 1, 'Frames are live HTML',
      `<p>This card is real DOM — text selects, inputs type, animations run. Drag its titlebar to move it.</p>`,
      { kind: 'frame', frameId: 'welcome' }),
    note('n-b2', 80, 580, 230, 'tour-guide', 2, '⌘K spawns anything',
      `<p>Press <b>⌘K</b> anywhere to drop a chat bubble at your cursor. Right-click empty canvas to add a TSX component, styleguide, or this onboarding. (Global gesture — no fixed spot on the canvas.)</p>`,
      undefined),
    note('n-b3', 80, 880, 230, 'tour-guide', 3, 'The action chip',
      `<p>Bottom-center pill: undo/redo, zoom %, theme toggle, and a ⋯ menu (export/import, render mode, annotations).</p>`,
      undefined),

    // ── Col 1 (Living Components) ──────────────────────────── x=780
    main('hero-main', 780, 80, 320,
      `<div style="padding:24px;font-family:system-ui;border-radius:8px;background:#0f172a;color:#fff">
        <strong data-slot="brand" style="font-size:20px">Acme</strong>
        <p style="margin:8px 0 14px;color:#94a3b8">Ship faster with one platform.</p>
        <button data-slot="cta" data-cf-id="hero-cta" style="padding:8px 16px;border:0;border-radius:6px;background:#22d3ee;color:#042f2a;font-weight:700">Get started</button>
      </div>`,
      ['brand', 'cta']),
    instance('hero-acme', 780, 260, 320, 'hero-main', { brand: 'Acme', cta: 'Get started' }),
    instance('hero-globex', 780, 440, 320, 'hero-main', { brand: 'Globex', cta: 'Request demo' }),
    main('price-main', 780, 620, 200,
      `<div style="padding:18px;font-family:system-ui;border:1px solid #e2e8f0;border-radius:8px;text-align:center">
        <div data-slot="tier" style="font-weight:700">Starter</div>
        <div data-slot="price" style="font-size:28px;margin:6px 0">$9</div>
        <div data-slot="note" style="color:#64748b;font-size:13px">per month</div>
      </div>`,
      ['tier', 'price', 'note']),
    instance('price-1', 780, 800, 200, 'price-main', { tier: 'Starter', price: '$9', note: 'per month' }),
    instance('price-2', 1000, 800, 200, 'price-main', { tier: 'Pro', price: '$29', note: 'per month' }),
    instance('price-3', 1220, 800, 200, 'price-main', { tier: 'Team', price: '$99', note: 'per month' }),
    note('n-c1', 780, 1060, 240, 'tour-guide', 4, 'Main components',
      `<p><b>hero-main</b> is a Main (★). Edit it once and every instance updates. Right-click a free frame → "Make Component".</p>`,
      { kind: 'frame', frameId: 'hero-main' }),
    note('n-c2', 1040, 1060, 240, 'tour-guide', 5, 'Instances + slots',
      `<p>These two heroes are instances of one Main. Only their <code>data-slot</code> text differs (brand, cta). This pin points at one <b>sub-element</b> — the CTA button itself.</p>`,
      { kind: 'element', frameId: 'hero-acme', nodeId: 'hero-cta' }),
    note('n-c3', 780, 1360, 240, 'tour-guide', 6, 'One Main, many instances',
      `<p>Three pricing cards, one source of truth. Change the Main's layout and all tiers follow.</p>`,
      { kind: 'frame', frameId: 'price-main' }),

    // ── Col 2 (TSX components) ─────────────────────────────── x=1480
    tsxFree('tsx-demo', 1480, 80, 340,
      `import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@/ui';

export default function Demo() {
  return (
    <Card className="w-[300px]">
      <CardHeader>
        <CardTitle>TSX Frame</CardTitle>
        <Badge>@/ui</Badge>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-slate-500">
          Live React component — edit the source in the Editor Panel.
        </p>
        <Button>Click me</Button>
      </CardContent>
    </Card>
  );
}`),
    tsxMain('feat-main', 1480, 400, 280,
      `import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@/ui';

export const props = {
  title: { type: 'text', default: 'Analytics' },
  desc: { type: 'text', default: 'Real-time dashboards' },
  badge: { type: 'text', default: 'New' },
  cta: { type: 'text', default: 'Learn more' },
  variant: { type: 'enum', default: 'default', options: ['default', 'secondary', 'destructive'] },
};

export default function Feature({ title, desc, badge, cta, variant }) {
  return (
    <Card className="w-60">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <Badge>{badge}</Badge>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-slate-500">{desc}</p>
        <Button variant={variant}>{cta}</Button>
      </CardContent>
    </Card>
  );
}`,
      [
        { name: 'title', type: 'text', default: 'Analytics' },
        { name: 'desc', type: 'text', default: 'Real-time dashboards' },
        { name: 'badge', type: 'text', default: 'New' },
        { name: 'cta', type: 'text', default: 'Learn more' },
        { name: 'variant', type: 'enum', default: 'default', options: ['default', 'secondary', 'destructive'] },
      ]),
    tsxInstance('feat-i1', 1480, 700, 280, 'feat-main', {}),
    tsxInstance('feat-i2', 1480, 1000, 280, 'feat-main',
      { title: 'Billing', desc: 'Usage-based invoicing', badge: 'Beta', cta: 'Open', variant: 'secondary' }),
    tsxInstance('feat-i3', 1480, 1300, 280, 'feat-main',
      { title: 'Alerts', desc: 'Anomaly detection', badge: 'Pro', cta: 'Upgrade', variant: 'destructive' }),
    note('n-x1', 1480, 1640, 240, 'tour-guide', 7, 'TSX frames + @/ui',
      `<p><b>TSX frames</b> render live React. Import the full shadcn-style library from <code>@/ui</code> — Button, Card, Input, Dialog, DropdownMenu, Popover, Tooltip, Accordion, Tabs, and more — already bundled. Add one from <b>⌘P → New TSX component</b> (⇧⌘N).</p>`,
      { kind: 'frame', frameId: 'tsx-demo' }),
    note('n-x2', 1480, 1940, 240, 'tour-guide', 8, 'A TSX Main',
      `<p><b>feat-main</b> is a TSX Main (★). It declares an <code>export const props</code> manifest — typed props (text, number, boolean, enum) that <i>replace</i> data-slot text.</p>`,
      { kind: 'frame', frameId: 'feat-main' }),
    note('n-x3', 1480, 2240, 240, 'tour-guide', 9, 'Instances override props',
      `<p>These three are instances of one Main. Each sets different prop values — including the Button's <code>variant</code> enum. Edit props in the Editor Panel's props form (select an instance).</p>`,
      { kind: 'frame', frameId: 'feat-i3' }),
    note('n-x4', 1480, 2540, 240, 'tour-guide', 10, 'Make your own',
      `<p>Write a <code>tsxFree</code> frame, then <b>right-click → Promote to component</b> (or drag-to-promote) to get a Main + a live instance. The agent can do all of this too.</p>`,
      undefined),

    // ── Col 3 (Agent + code) ───────────────────────────────── x=2180
    free('agent-target', 2180, 80, 320,
      `<div style="padding:20px;font-family:system-ui">
        <h3 style="margin:0 0 8px">Testimonials</h3>
        <p style="margin:0;color:#555">Ask the agent to add a section, restyle, or generate variants.</p>
      </div>`),
    note('n-a1', 2180, 260, 240, 'tour-guide', 11, 'Design with the agent',
      `<p>Open a <b style="color:#10b981">chat</b> tool frame anchored to a design frame (right-click → "Open chat → here"). Try: "add a 3-up testimonial grid".</p>`,
      { kind: 'frame', frameId: 'agent-target' }),
    note('n-a2', 2180, 560, 240, 'tour-guide', 12, 'Code + scope',
      `<p>The <b style="color:#f59e0b">code</b> tool frame projects a frame's HTML for direct edits. Anchored tools act on one frame; unanchored act on the whole canvas.</p>`,
      undefined),
    note('n-a3', 2180, 860, 240, 'tour-guide', 13, 'TSX frames + @/ui',
      `<p><b>TSX frames</b> render live React components. Import from <code>@/ui</code> to use shadcn-style primitives — Button, Card, Badge — already bundled. Promote a tsxFree to a <b>Main</b> with typed props for reusable instances.</p>`,
      { kind: 'frame', frameId: 'tsx-demo' }),

    // ── Col 4 (Effects) ────────────────────────────────────── x=2880
    fxPoster('fx-glow',      2880, 80,  { kind: 'glow', color: '#22d3ee', radius: 16, passes: 3 }, 'glow'),
    fxPoster('fx-outline',   3100, 80,  { kind: 'outline', color: '#000000', width: 3 }, 'outline'),
    fxPoster('fx-crt',       3320, 80,  { kind: 'crt', intensity: 0.6 }, 'crt'),
    fxPoster('fx-chromatic', 2880, 300, { kind: 'chromatic', offset: 3 }, 'chromatic'),
    fxPoster('fx-frosted',   3100, 300, { kind: 'frostedGlass', blur: 8, tint: 'rgba(255,255,255,0.15)' }, 'frostedGlass'),
    fxPoster('fx-blur',      3320, 300, { kind: 'blur', px: 4 }, 'blur'),
    note('n-e1', 2880, 540, 240, 'tour-guide', 14, 'Effects gallery',
      `<p>One poster, many looks: glow, outline, crt, chromatic, frostedGlass, blur, grayscale. Effects paint real pixels in canvas mode (Chrome Canary 147+).</p>`,
      { kind: 'frame', frameId: 'fx-glow' }),
    note('n-e2', 2880, 840, 240, 'tour-guide', 15, 'The fx tool frame',
      `<p>Open an <b style="color:#a855f7">fx</b> tool frame on any design frame to pick the effect kind + intensity with a slider.</p>`,
      { kind: 'frame', frameId: 'fx-crt' }),

    // ── Col 5 (Annotations) ────────────────────────────────── x=3580
    note('n-m1', 3580, 80, 250, 'tour-guide', 16, 'These cards are notes',
      `<p>Annotations are a tool-frame kind. Add one from the canvas/frame menu. They never export with your design unless you keep them.</p>`,
      undefined),
    note('n-m2', 3580, 380, 250, 'tour-guide', 17, 'Three target kinds',
      `<p>A note can point at a <b>whole frame</b> (dotted leader), a <b>canvas pin</b> (free x/y), or a <b>sub-element</b> inside a frame (see the pin on the CTA button in step 5).</p>`,
      undefined),
    note('n-m3', 3580, 680, 250, 'tour-guide', 18, 'Build a tour',
      `<p>Give notes the same tour + step numbers to build a guided walkthrough. The player up top steps the camera through each one.</p>`,
      undefined),
    note('n-m4', 3580, 980, 250, 'tour-guide', 19, 'Hide the layer',
      `<p>Toggle annotations off from the action chip's ⋯ menu when you want a clean view of just the design.</p>`,
      undefined),

    // ── Col 6 (Themes & styling) ───────────────────────────── x=4280
    themed('themed-card', 4280, 80, 320,
      `<div class="bg-surface text-body rounded-card p-gutter" style="border:1px solid var(--color-border)">
        <h3 class="text-display font-display">Tokens, not hardcodes</h3>
        <p>This card uses Tailwind utilities (<code>bg-surface</code>, <code>rounded-card</code>, <code>p-gutter</code>) that resolve from the active theme.</p>
        <button class="bg-primary rounded-card" style="color:#fff;padding:8px 14px;border:0">Primary</button>
      </div>`,
      'cf-brand'),
    themed('scripted-counter', 4280, 340, 240,
      `<div class="bg-surface rounded-card p-gutter" style="border:1px solid var(--color-border);text-align:center">
        <div data-count style="font-size:32px;font-weight:700">0</div>
        <button data-inc class="bg-primary rounded-card" style="color:#fff;padding:6px 12px;border:0">+1</button>
      </div>`,
      'cf-brand',
      `export default (root) => {
        const out = root.querySelector('[data-count]');
        const btn = root.querySelector('[data-inc]');
        let n = 0;
        const on = () => { n++; if (out) out.textContent = String(n); };
        btn?.addEventListener('click', on);
        return () => btn?.removeEventListener('click', on);
      }`),
    styleguideFrame('sg-brand', 4280, 600, 'cf-brand'),
    note('n-t1', 4280, 980, 240, 'tour-guide', 20, 'Active theme',
      `<p>This canvas has an <b>active theme</b> (set it from ⌘P → "Active theme: …"). Frames with no theme of their own inherit it, and the agent styles new work with its tokens.</p>`,
      undefined),
    note('n-t2', 4280, 1280, 240, 'tour-guide', 21, 'Styleguide node',
      `<p>The <b style="color:#a855f7">styleguide</b> tool frame visualizes a theme's colors, type scale, radii, spacing, and components — live. Add one from the canvas right-click menu.</p>`,
      { kind: 'frame', frameId: 'sg-brand' }),
    note('n-t3', 4280, 1580, 240, 'tour-guide', 22, 'Tailwind utilities',
      `<p>Style frames with utility classes like <code>bg-primary</code> / <code>rounded-card</code>; they compile at runtime from theme tokens. Browse 70+ ready themes via the Editor Panel's <b>▤ Library</b>, or ask the agent to <b>generate</b> one.</p>`,
      { kind: 'frame', frameId: 'themed-card' }),
    note('n-t4', 4280, 1880, 240, 'tour-guide', 23, 'Scripts add behavior',
      `<p>Frames can carry a single-file TypeScript <code>setup(root, ctx)</code> behavior (Editor Panel → Script tab). This counter increments on click.</p>`,
      { kind: 'frame', frameId: 'scripted-counter' }),

    // ── Col 7 (Layers, Feedback & Motion) ──────────────────── x=4980
    free('layers-demo', 4980, 80, 320,
      `<div style="padding:16px;font-family:system-ui;display:flex;flex-direction:column;gap:8px">
        <div style="padding:10px;background:#eef2ff;border-radius:6px">① First block</div>
        <div data-cf-id="layer-block-2" style="padding:10px;background:#fae8ff;border-radius:6px">② Second block</div>
        <div style="padding:10px;background:#dcfce7;border-radius:6px">③ Third block</div>
        <button style="padding:8px 14px;border:0;border-radius:6px;background:#6366f1;color:#fff">A button</button>
      </div>`),
    note('n-l1', 4980, 460, 240, 'tour-guide', 24, 'Select a layer',
      `<p><b>Option-click</b> (⌥) any element inside a frame to select that <b>sub-element</b> — you get an outline, a breadcrumb of its ancestors, and an action bar. Arrow keys step in / out / sideways through the tree; Esc steps out.</p>`,
      { kind: 'element', frameId: 'layers-demo', nodeId: 'layer-block-2' }),
    note('n-l2', 4980, 760, 240, 'tour-guide', 25, 'Drag to reorder',
      `<p>Grab the <b>⋮⋮ grip</b> on the layer action bar and drag a block above or below a sibling — or into a container. A line shows where it lands; release to commit (one undo step). The node keeps its id, so motion and comments stay attached.</p>`,
      { kind: 'frame', frameId: 'layers-demo' }),
    note('n-l3', 4980, 1060, 240, 'tour-guide', 26, 'Ask the agent about a layer',
      `<p>With a layer selected, click <b>✦ Ask agent</b>. It opens a chat scoped to <i>just that subtree</i> — the agent sees the exact markup and edits only within it. Replies stream live and the conversation is multi-turn.</p>`,
      { kind: 'frame', frameId: 'layers-demo' }),
    note('n-l4', 4980, 1360, 240, 'tour-guide', 27, 'Leave a comment',
      `<p>Click the <b>comment</b> icon on the layer action bar (or a frame) to pin a feedback thread. The agent reads open threads as context and can reply or resolve them.</p>`,
      undefined),
    note('n-l5', 4980, 1660, 240, 'tour-guide', 28, 'Animate it (⌘T)',
      `<p>Press <b>⌘T</b> (or the Film button on the action chip) to open the <b>motion timeline</b>. Add keyframes on a frame's nodes — transforms, opacity, colors — then drive them with autoplay, scroll, hover, or click triggers.</p>`,
      { kind: 'frame', frameId: 'layers-demo' }),
    note('n-l6', 4980, 1960, 240, 'tour-guide', 29, 'History & checkpoints',
      `<p>Every edit is versioned. Restore a single frame from its history, or roll the whole canvas back to a named <b>checkpoint</b> — each as one undoable step. Open them from the action chip's ⋯ menu.</p>`,
      undefined),
  ];

  const themes: ThemeToken[] = [
    {
      id: 'cf-brand',
      name: 'Codeframe Brand',
      colors: { primary: '#6366f1', surface: '#ffffff', body: '#1e293b' },
      typography: { display: { fontFamily: 'system-ui', fontSize: '20px', fontWeight: 700 }, body: { fontFamily: 'system-ui', fontSize: '14px' } },
      rounded: { card: '10px' },
      spacing: { gutter: '20px' },
    },
  ];
  return { version: 2, frames, viewport: { pan: { x: 0, y: 0 }, zoom: 0.5 }, tours, themes, activeThemeId: 'cf-brand' };
}

// builders
let zc = 0;
const z = () => ++zc;

function free(id: string, x: number, y: number, w: number, html: string): Frame {
  return { kind: 'free', id, x, y, w, z: z(), title: id, effect: none, html };
}
function main(id: string, x: number, y: number, w: number, html: string, slotNames: string[]): Frame {
  return {
    kind: 'main', id, x, y, w, z: z(), title: id, effect: none, html, locked: false,
    slots: slotNames.map((name) => ({ name, defaultText: name, cfNodeId: `${id}-${name}` })),
  };
}
function instance(id: string, x: number, y: number, w: number, mainId: string, slotValues: Record<string, string>): Frame {
  return { kind: 'instance', id, x, y, w, z: z(), title: id, effect: none, mainId, slotValues };
}
function fxPoster(id: string, x: number, y: number, effect: Effect, label: string): Frame {
  return {
    kind: 'free', id, x, y, w: 200, z: z(), title: label, effect,
    html: `<div style="padding:18px;font-family:system-ui;background:linear-gradient(135deg,#6366f1,#ec4899);color:#fff;border-radius:8px;text-align:center;height:120px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:18px">${label}</div>`,
  };
}
function note(
  id: string, x: number, y: number, w: number, tourId: string, step: number, title: string, html: string,
  target: NoteTarget | undefined,
): Frame {
  return { kind: 'note', id, x, y, w, z: z(), title, effect: none, html, tourId, step, target };
}
function styleguideFrame(id: string, x: number, y: number, themeId: string): Frame {
  return { kind: 'styleguide', id, x, y, w: 320, z: z(), title: 'styleguide', effect: none, themeId };
}
function themed(id: string, x: number, y: number, w: number, html: string, themeId: string, script?: string): Frame {
  return { kind: 'free', id, x, y, w, z: z(), title: id, effect: none, html, themeId, ...(script ? { script } : {}) };
}
function tsxFree(id: string, x: number, y: number, w: number, tsx: string): Frame {
  return { kind: 'tsxFree', id, x, y, w, z: z(), title: id, effect: none, tsx };
}
function tsxMain(id: string, x: number, y: number, w: number, tsx: string, propsSchema: PropSpec[]): Frame {
  return { kind: 'tsxMain', id, x, y, w, z: z(), title: id, effect: none, tsx, propsSchema, locked: false };
}
function tsxInstance(id: string, x: number, y: number, w: number, mainId: string, propValues: Record<string, PropValue>): Frame {
  return { kind: 'tsxInstance', id, x, y, w, z: z(), title: id, effect: none, mainId, propValues };
}
